import { Link } from 'react-router-dom';
import { useStore } from '../lib/useStore.jsx';
import { appOf, today } from '../lib/store.js';
import { SKILLS, BRANCHES } from '../content/skills.js';
import { visibleSkills, progressOf, suggestNext, byBranch, isOwned, ownedCount } from '../domain/skills.js';
import { StateChip } from '../ui/Bits.jsx';
import { DoorIcon } from '../ui/Icons.jsx';

function daysAgo(dateOnly, now) {
  return Math.round((Date.parse(now) - Date.parse(dateOnly)) / 86400000);
}

function SkillCard({ skill, progress, color, now }) {
  const owned = isOwned(progress.state);
  const fresh = owned && progress.verifiedAt && daysAgo(progress.verifiedAt, now) <= 7;
  const cls = ['skill-card', progress.started && 'started', owned && 'owned', `c-${color}`, skill.safety !== 'free' && 'adult']
    .filter(Boolean).join(' ');
  return (
    <Link to={`/skill/${skill.id}`} className={cls}>
      <span className="title">{owned ? skill.canDo : skill.title}</span>
      <span className="row" style={{ gap: 6 }}><StateChip state={progress.state} />{fresh && <span className="chip new">New!</span>}</span>
      {owned && progress.verifiedAt && <span className="small muted">since {progress.verifiedAt}</span>}
    </Link>
  );
}

export default function Wall() {
  const { active } = useStore();
  if (!active) return null;
  const app = appOf(active);
  const skills = visibleSkills(SKILLS, app);
  const next = suggestNext(SKILLS, app, app.events, 3);
  const owned = ownedCount(skills, app.events);
  const now = today();

  if (skills.length === 0) {
    return (
      <div className="empty">
        <DoorIcon className="door" />
        <h2>Nothing on {active.name}'s wall yet</h2>
        <p className="muted">A grown-up picks the first skills under <b>Grown-ups</b>.</p>
        <Link to="/parent" className="btn primary">Choose skills</Link>
      </div>
    );
  }

  const groups = byBranch(skills);
  return (
    <div className="stack">
      <div className="spread">
        <div>
          <p className="eyebrow">{active.name}'s wall</p>
          <h1>{owned === 0 ? 'What can you do?' : `${owned} thing${owned === 1 ? '' : 's'} I can do`}</h1>
        </div>
      </div>

      {next.length > 0 && (
        <section className="card tint-yellow">
          <p className="eyebrow">Next up</p>
          <div className="stack" style={{ marginTop: 8 }}>
            {next.map((s) => (
              <Link key={s.id} to={`/skill/${s.id}`} className="btn block" style={{ justifyContent: 'space-between' }}>
                <span>{s.title}</span><span className="muted small">{BRANCHES.find((b) => b.id === s.branch)?.name}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {BRANCHES.filter((b) => groups.has(b.id)).map((b) => (
        <section key={b.id} className="branch">
          <div className="branch-head">
            <span className="swatch" style={{ background: `var(--${b.color})` }} />
            <h2>{b.name}</h2>
            <span className="muted small">{b.tagline}</span>
          </div>
          <div className="grid">
            {groups.get(b.id).map((s) => (
              <SkillCard key={s.id} skill={s} progress={progressOf(app.events, s.id)} color={b.color} now={now} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
